import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { productionAPI, qcAPI, standaloneAlterationsAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  DndContext, DragOverlay, useDroppable,
  PointerSensor, useSensor, useSensors, closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, Legend,
} from 'recharts';
import {
  Play, CheckCircle2, AlertTriangle, Package, Clock, User, ChevronRight,
  XCircle, LayoutGrid, CalendarDays, GitBranch, RefreshCw, RotateCcw, GripVertical,
  ClipboardCheck, Activity, Truck, Building2, Printer,
} from 'lucide-react';
import QCInspectionForm from '@/components/QCInspectionForm';
import { generateProductionSheetPDF, downloadPDF } from '@/lib/pdfGenerator';
import { HelpTooltip } from '@/components/HelpTooltip';

const TASK_TYPE_LABELS = { pattern_draft: 'Pattern Draft', pattern_cut: 'Pattern Cut', stitching: 'Stitching', finishing: 'Finishing', correction: 'Correction' };
const STATUS_CFG = {
  queued: { label: 'Queued', bg: 'rgba(37,99,235,0.08)', text: '#2563EB', icon: Clock },
  waiting_fabric: { label: 'Waiting Fabric', bg: 'rgba(107,114,128,0.15)', text: '#6B7280', icon: Package },
  in_progress: { label: 'In Progress', bg: 'rgba(245,158,11,0.12)', text: '#D97706', icon: Play },
  completed: { label: 'Completed', bg: 'rgba(16,185,129,0.08)', text: '#059669', icon: CheckCircle2 },
  blocked: { label: 'Blocked', bg: 'rgba(239,68,68,0.12)', text: '#DC2626', icon: XCircle },
  cancelled: { label: 'Cancelled', bg: 'rgba(107,114,128,0.06)', text: '#9CA3AF', icon: XCircle },
};
const GARMENT_LABELS = { jacket: 'Jacket', trousers: 'Trousers', shirt: 'Shirt', sherwani: 'Sherwani', bandhgala: 'Bandhgala', nehru_vest: 'Nehru Vest', kurta: 'Kurta', pyjama: 'Pyjama', waistcoat: 'Waistcoat' };
const GARMENT_COLORS = { jacket: '#2563EB', trousers: '#059669', shirt: '#D97706', sherwani: '#9333EA', bandhgala: '#2563EB', nehru_vest: '#0891B2', kurta: '#D97706', pyjama: '#059669', waistcoat: '#0891B2' };
const ORDER_STATUS_LABELS = { confirmed: 'Confirmed', fabric_ordered: 'Fabric Ordered', fabric_received: 'Fabric Received', in_production: 'In Production', ready_for_trial: 'Ready for Trial', alterations_in_progress: 'Alterations', ready_for_delivery: 'Ready' };

/* ========== Sortable Task Card ========== */
function DraggableTaskCard({ task, onAction, canEdit }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id });
  const isAltTask = task.task_type === 'standalone_alteration';
  const gc = isAltTask ? '#7C3AED' : (GARMENT_COLORS[task.garment_type] || '#6B7280');
  const sc = STATUS_CFG[task.status] || (isAltTask ? { bg: 'rgba(124,58,237,0.08)', text: '#7C3AED', label: task.status } : STATUS_CFG.queued);
  const initials = (task.client_name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const taskLabel = isAltTask ? task.alteration_number : task.order_number;
  const taskLink = isAltTask ? `/app/alterations/${task.id}` : `/app/orders/${task.order_id}`;

  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };

  // Status-based card styling
  const isWaitingFabric = task.status === 'waiting_fabric';
  const isQueued = task.status === 'queued';
  const isInProgress = task.status === 'in_progress';
  const isCompleted = task.status === 'completed';
  const isBlocked = task.status === 'blocked';

  // Card background based on status
  const cardBg = isWaitingFabric ? 'rgba(107,114,128,0.04)' 
    : isQueued ? 'rgba(37,99,235,0.02)'
    : isInProgress ? 'rgba(245,158,11,0.04)'
    : isCompleted ? 'rgba(16,185,129,0.03)'
    : isBlocked ? 'rgba(239,68,68,0.04)'
    : 'white';

  const borderStyle = task.is_express
    ? { borderColor: '#D97706', borderWidth: '2px', borderStyle: 'solid', borderLeftWidth: '3px', borderLeftColor: '#D97706' }
    : isAltTask
    ? { borderColor: '#7C3AED', borderWidth: '2px', borderStyle: 'solid', borderLeftWidth: '3px', borderLeftColor: '#7C3AED' }
    : isBlocked
    ? { borderColor: '#DC2626', borderWidth: '1px', borderStyle: 'solid' }
    : isWaitingFabric
    ? { borderColor: '#9CA3AF', borderWidth: '1px', borderStyle: 'dashed' }
    : {};

  return (
    <div ref={setNodeRef} style={{ ...style, borderLeft: `3px solid ${gc}`, backgroundColor: cardBg, ...borderStyle }}
      className={`p-2.5 rounded-lg border hover:shadow-md group select-none ${isDragging ? 'shadow-lg z-50' : ''} ${isWaitingFabric ? 'opacity-70' : ''}`}
      data-testid={`task-card-${task.id}`}>
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          {canEdit && (
            <div {...listeners} {...attributes} className="cursor-grab active:cursor-grabbing touch-none p-0.5 -ml-1 rounded hover:bg-gray-100 flex-shrink-0" data-testid={`drag-handle-${task.id}`}>
              <GripVertical className="w-3 h-3" style={{ color: 'var(--ink-muted)' }} />
            </div>
          )}
          <div className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 text-[8px] font-bold" style={{ backgroundColor: gc + '15', color: gc }}>{initials}</div>
          <div className="min-w-0">
            <div className="flex items-center gap-1">
              <Link to={taskLink} className="text-[10px] font-bold hover:underline block truncate" style={{ color: 'var(--brand-navy)' }}>{taskLabel}</Link>
              {task.is_express && <span className="text-[7px] font-bold px-1 rounded" style={{ backgroundColor: '#D97706', color: '#fff' }} data-testid={`express-tag-${task.id}`}>EXPRESS</span>}
              {isAltTask && <span className="text-[7px] font-bold px-1 rounded" style={{ backgroundColor: '#7C3AED', color: '#fff' }} data-testid={`alt-tag-${task.id}`}>ALT</span>}
            </div>
            <p className="text-[9px] truncate" style={{ color: 'var(--ink-muted)' }}>
              {isAltTask ? (task.item_summary || GARMENT_LABELS[task.garment_type]) : (GARMENT_LABELS[task.garment_type] || task.garment_type)}
            </p>
          </div>
        </div>
        <Badge variant="outline" className="text-[8px] px-1 py-0 rounded-sm whitespace-nowrap ml-1 flex-shrink-0"
          style={{ backgroundColor: sc.bg, color: sc.text, borderColor: sc.text + '30' }}>{sc.label}</Badge>
      </div>
      <p className="text-[10px] font-semibold" style={{ color: 'var(--navy-ink)' }}>
        {isAltTask ? `${task.client_name || 'Walk-in'} · ${GARMENT_LABELS[task.garment_type] || ''}` : TASK_TYPE_LABELS[task.task_type]}
      </p>
      {task.is_outsourced && (
        <div className="mt-0.5 space-y-0.5">
          {task.outsource_sent_date && (
            <p className="text-[9px]" style={{ color: 'var(--ink-muted)' }}>Sent: {task.outsource_sent_date}</p>
          )}
          {task.outsource_expected_return && (() => {
            const today = new Date(); today.setHours(0,0,0,0);
            const exp = new Date(task.outsource_expected_return); exp.setHours(0,0,0,0);
            const diff = Math.ceil((exp - today) / (1000*60*60*24));
            return diff < 0
              ? <p className="text-[9px] font-bold" style={{ color: '#DC2626' }}>OVERDUE by {Math.abs(diff)} day{Math.abs(diff)!==1?'s':''}</p>
              : <p className="text-[9px]" style={{ color: diff <= 2 ? '#D97706' : 'var(--ink-muted)' }}>Expected back: {task.outsource_expected_return} ({diff}d)</p>;
          })()}
        </div>
      )}
      {!task.is_outsourced && task.required_by_date && <p className="text-[9px] mt-0.5" style={{ color: 'var(--ink-muted)' }}>Due: {task.required_by_date}</p>}
      {/* Action buttons based on status */}
      {canEdit && task.status !== 'completed' && task.status !== 'cancelled' && (
        <div className="flex flex-wrap gap-1 mt-1.5 opacity-0 group-hover:opacity-100" style={{ transition: 'opacity 100ms' }}>
          {/* Outsource actions */}
          {task.is_outsourced && !task.outsource_sent_date && (
            <Button size="sm" className="h-5 text-[9px] px-1.5 gap-0.5 rounded-sm" style={{ backgroundColor: '#6B7280' }}
              onClick={() => onAction(task, 'outsource_send')} data-testid={`task-outsource-send-${task.id}`}>
              <Truck className="w-2.5 h-2.5" /> Mark Sent
            </Button>
          )}
          {task.is_outsourced && task.outsource_sent_date && !task.outsource_actual_return && (
            <Button size="sm" className="h-5 text-[9px] px-1.5 gap-0.5 rounded-sm" style={{ backgroundColor: '#059669' }}
              onClick={() => onAction(task, 'outsource_receive')} data-testid={`task-outsource-receive-${task.id}`}>
              <CheckCircle2 className="w-2.5 h-2.5" /> Mark Received
            </Button>
          )}
          {/* QUEUED: Show prominent START button */}
          {!task.is_outsourced && task.status === 'queued' && (
            <Button size="sm" className="h-6 text-[10px] px-2 gap-0.5 rounded-sm font-semibold" style={{ backgroundColor: '#2563EB' }}
              onClick={() => onAction(task, 'start')} data-testid={`task-start-${task.id}`}>
              <Play className="w-3 h-3" /> START
            </Button>
          )}
          {/* WAITING_FABRIC: Show greyed out state, only BLOCK is available */}
          {!task.is_outsourced && task.status === 'waiting_fabric' && (
            <span className="text-[9px] px-2 py-0.5 rounded-sm bg-gray-100 text-gray-500">Waiting for fabric...</span>
          )}
          {/* IN_PROGRESS: Show COMPLETE button */}
          {task.status === 'in_progress' && (
            <Button size="sm" className="h-6 text-[10px] px-2 gap-0.5 rounded-sm font-semibold" style={{ backgroundColor: '#059669' }}
              onClick={() => onAction(task, 'complete')} data-testid={`task-complete-${task.id}`}>
              <CheckCircle2 className="w-3 h-3" /> COMPLETE
            </Button>
          )}
          {/* BLOCK button (available for queued, in_progress, waiting_fabric) */}
          {task.status !== 'blocked' && (
            <Button size="sm" variant="outline" className="h-5 text-[9px] px-1.5 gap-0.5 rounded-sm"
              onClick={() => onAction(task, 'block')} data-testid={`task-block-${task.id}`}>
              <XCircle className="w-2.5 h-2.5" style={{ color: '#DC2626' }} /> Block
            </Button>
          )}
          {/* BLOCKED: Show UNBLOCK button */}
          {task.status === 'blocked' && (
            <Button size="sm" className="h-6 text-[10px] px-2 gap-0.5 rounded-sm font-semibold" style={{ backgroundColor: '#DC2626' }}
              onClick={() => onAction(task, 'unblock')} data-testid={`task-unblock-${task.id}`}>
              <RotateCcw className="w-3 h-3" /> UNBLOCK
            </Button>
          )}
        </div>
      )}
      {/* QC button for completed stitching/finishing/correction tasks */}
      {canEdit && task.status === 'completed' && ['stitching', 'correction'].includes(task.task_type) && (
        <div className="mt-1.5">
          <Button size="sm" className="h-5 text-[9px] px-1.5 gap-0.5 rounded-sm" style={{ backgroundColor: 'var(--brand-gold)' }}
            onClick={() => onAction(task, 'qc_pre_trial')} data-testid={`task-qc-${task.id}`}>
            <ClipboardCheck className="w-2.5 h-2.5" /> Run QC
          </Button>
        </div>
      )}
    </div>
  );
}

/* ========== Drag Overlay Card (ghost while dragging) ========== */
function OverlayCard({ task }) {
  if (!task) return null;
  const gc = GARMENT_COLORS[task.garment_type] || '#6B7280';
  const sc = STATUS_CFG[task.status] || STATUS_CFG.queued;
  return (
    <div className="p-2.5 rounded-lg border bg-white shadow-xl w-[230px] pointer-events-none"
      style={{ borderLeft: `3px solid ${gc}`, borderColor: task.is_express ? '#D97706' : 'rgba(27,42,74,0.12)', transform: 'rotate(2deg)', ...(task.is_express ? { borderWidth: '2px' } : {}) }}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1">
          <span className="text-[10px] font-bold" style={{ color: 'var(--brand-navy)' }}>{task.order_number}</span>
          {task.is_express && <span className="text-[7px] font-bold px-1 rounded" style={{ backgroundColor: '#D97706', color: '#fff' }}>EXPRESS</span>}
        </div>
        <Badge variant="outline" className="text-[8px] px-1 py-0 rounded-sm" style={{ backgroundColor: sc.bg, color: sc.text }}>{sc.label}</Badge>
      </div>
      <p className="text-[10px] font-semibold" style={{ color: 'var(--navy-ink)' }}>{TASK_TYPE_LABELS[task.task_type]}</p>
      <p className="text-[9px]" style={{ color: 'var(--ink-muted)' }}>{GARMENT_LABELS[task.garment_type] || task.garment_type}</p>
    </div>
  );
}

/* ========== Droppable Worker Column ========== */
function WorkerColumn({ workerId, workerName, tasks, onAction, canEdit, isOver, isOutsourceVendor }) {
  const { setNodeRef } = useDroppable({ id: workerId });
  const taskIds = useMemo(() => tasks.map(t => t.id), [tasks]);
  const headerBg = isOutsourceVendor ? 'rgba(107,114,128,0.10)' : 'rgba(27,42,74,0.08)';
  const headerColor = isOutsourceVendor ? '#4B5563' : 'var(--brand-navy)';
  return (
    <div ref={setNodeRef}
      className="min-w-[230px] max-w-[250px] flex-shrink-0 rounded-xl p-2 transition-colors duration-150"
      style={{ backgroundColor: isOver ? 'rgba(197,165,90,0.08)' : 'transparent', border: isOver ? '2px dashed rgba(197,165,90,0.5)' : '2px solid transparent' }}
      data-testid={`worker-column-${workerId}`}>
      <div className="flex items-center gap-2 mb-2 px-1">
        <div className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold"
          style={{ backgroundColor: headerBg, color: headerColor }}>
          {isOutsourceVendor ? <Building2 className="w-3 h-3" /> : workerName?.[0]?.toUpperCase() || '?'}
        </div>
        <span className="text-xs font-semibold truncate" style={{ color: headerColor }}>{workerName}</span>
        {isOutsourceVendor && <span className="text-[8px] font-bold px-1 rounded" style={{ backgroundColor: 'rgba(107,114,128,0.1)', color: '#6B7280' }}>VENDOR</span>}
        <Badge variant="outline" className="text-[9px] px-1 py-0 ml-auto flex-shrink-0">{tasks.length}</Badge>
      </div>
      <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
        <div className="space-y-1.5 min-h-[60px]">
          {tasks.map(t => <DraggableTaskCard key={t.id} task={t} onAction={onAction} canEdit={canEdit} />)}
          {tasks.length === 0 && (
            <div className="py-4 text-center border border-dashed rounded-lg" style={{ borderColor: 'rgba(27,42,74,0.1)' }}>
              <p className="text-[9px]" style={{ color: 'var(--ink-muted)' }}>Drop tasks here</p>
            </div>
          )}
        </div>
      </SortableContext>
    </div>
  );
}

/* ========== Daily DnD Kanban ========== */
function DailyView({ tasks, workerList, onAction, canEdit, onReassign, onReorder }) {
  const [activeTask, setActiveTask] = useState(null);
  const [overColumn, setOverColumn] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const columns = useMemo(() => {
    const cols = {};
    const unassigned = [];
    for (const t of tasks) {
      if (!t.assigned_worker_id) { unassigned.push(t); continue; }
      if (!cols[t.assigned_worker_id]) cols[t.assigned_worker_id] = { id: t.assigned_worker_id, name: t.assigned_worker_name, tasks: [], role: 'karigar' };
      cols[t.assigned_worker_id].tasks.push(t);
    }
    // Ensure columns for all known workers per visibility rules
    for (const w of workerList) {
      if (!cols[w.id]) {
        // Masters: only show if they have active tasks (skip empty)
        if (w.role === 'master') continue;
        cols[w.id] = { id: w.id, name: w.name, tasks: [], role: w.role || 'karigar' };
      } else {
        cols[w.id].role = w.role || 'karigar';
      }
    }
    // Sort: express orders first within each column
    const sortExpress = (arr) => arr.sort((a, b) => (b.is_express ? 1 : 0) - (a.is_express ? 1 : 0));
    sortExpress(unassigned);
    Object.values(cols).forEach(col => sortExpress(col.tasks));
    return { cols, unassigned };
  }, [tasks, workerList]);

  const taskMap = useMemo(() => {
    const m = {};
    tasks.forEach(t => { m[t.id] = t; });
    return m;
  }, [tasks]);

  // Find which column (worker_id) a task belongs to
  const findColumnForTask = useCallback((taskId) => {
    for (const [wId, col] of Object.entries(columns.cols)) {
      if (col.tasks.some(t => t.id === taskId)) return wId;
    }
    return null;
  }, [columns]);

  const handleDragStart = (event) => {
    setActiveTask(taskMap[event.active.id] || null);
  };

  const handleDragOver = (event) => {
    const { over } = event;
    if (!over) { setOverColumn(null); return; }
    // over.id could be a worker column id or a task card id
    const isWorkerColumn = columns.cols[over.id] != null;
    if (isWorkerColumn) {
      setOverColumn(over.id);
    } else {
      // It's a task card — find which column it belongs to
      const colId = findColumnForTask(over.id);
      setOverColumn(colId);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveTask(null);
    setOverColumn(null);
    if (!over || !active) return;

    const draggedTask = taskMap[active.id];
    if (!draggedTask) return;

    const sourceColumnId = draggedTask.assigned_worker_id;
    
    // Determine target: is `over` a column or a task?
    const isWorkerColumn = columns.cols[over.id] != null;
    let targetColumnId;
    if (isWorkerColumn) {
      targetColumnId = over.id;
    } else {
      targetColumnId = findColumnForTask(over.id);
    }

    if (!targetColumnId) return;

    if (sourceColumnId !== targetColumnId) {
      // Inter-column: reassign to different worker
      onReassign(active.id, targetColumnId);
    } else if (active.id !== over.id && !isWorkerColumn) {
      // Intra-column reorder: same worker, different position
      const colTasks = columns.cols[sourceColumnId]?.tasks || [];
      const oldIndex = colTasks.findIndex(t => t.id === active.id);
      const newIndex = colTasks.findIndex(t => t.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        const reordered = arrayMove(colTasks, oldIndex, newIndex);
        onReorder(reordered.map(t => t.id));
      }
    }
  };

  const handleDragCancel = () => {
    setActiveTask(null);
    setOverColumn(null);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter}
      onDragStart={handleDragStart} onDragOver={handleDragOver}
      onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
      <div className="flex gap-2 overflow-x-auto pb-4" data-testid="daily-kanban-view">
        {columns.unassigned.length > 0 && (
          <div className="min-w-[230px] max-w-[250px] flex-shrink-0">
            <div className="flex items-center gap-2 mb-2 px-1">
              <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center"><User className="w-3 h-3 text-gray-400" /></div>
              <span className="text-xs font-semibold" style={{ color: 'var(--ink-muted)' }}>Unassigned</span>
              <Badge variant="outline" className="text-[9px] px-1 py-0 ml-auto">{columns.unassigned.length}</Badge>
            </div>
            <SortableContext items={columns.unassigned.map(t => t.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-1.5">
                {columns.unassigned.map(t => <DraggableTaskCard key={t.id} task={t} onAction={onAction} canEdit={canEdit} />)}
              </div>
            </SortableContext>
          </div>
        )}
        {Object.values(columns.cols).map(col => (
          <WorkerColumn key={col.id} workerId={col.id} workerName={col.name}
            tasks={col.tasks} onAction={onAction} canEdit={canEdit}
            isOver={overColumn === col.id}
            isOutsourceVendor={col.role === 'outsourced_vendor'} />
        ))}
        {Object.keys(columns.cols).length === 0 && columns.unassigned.length === 0 && (
          <div className="w-full py-12 text-center">
            <Package className="w-10 h-10 mx-auto mb-3 opacity-20" style={{ color: 'var(--brand-navy)' }} />
            <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>No active production tasks</p>
          </div>
        )}
      </div>
      <DragOverlay dropAnimation={{ duration: 200, easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)' }}>
        <OverlayCard task={activeTask} />
      </DragOverlay>
    </DndContext>
  );
}

/* ========== Capacity View ========== */
function WeeklyView({ workerLoads }) {
  if (!workerLoads || workerLoads.length === 0) return <p className="text-sm py-8 text-center" style={{ color: 'var(--ink-muted)' }}>No worker data</p>;
  return (
    <div className="space-y-3" data-testid="weekly-gantt-view">
      {workerLoads.map(w => (
        <div key={w.id} className="flex items-center gap-3 p-3 rounded-lg border" style={{ borderColor: 'rgba(27,42,74,0.06)' }}>
          <div className="w-28 flex-shrink-0">
            <p className="text-xs font-semibold truncate" style={{ color: 'var(--brand-navy)' }}>{w.name}</p>
            <p className="text-[10px] capitalize" style={{ color: 'var(--ink-muted)' }}>{w.role}</p>
          </div>
          <div className="flex-1 h-8 rounded-md relative overflow-hidden" style={{ backgroundColor: 'rgba(27,42,74,0.04)' }}>
            <div className="h-full rounded-md" style={{ width: `${Math.min(w.capacity_pct, 100)}%`, backgroundColor: w.capacity_pct > 100 ? '#DC2626' : w.capacity_pct > 80 ? '#D97706' : '#059669', transition: 'width 400ms ease' }} />
            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold"
              style={{ color: w.capacity_pct > 50 ? '#fff' : 'var(--navy-ink)' }}>
              {w.active_tasks} tasks &middot; {w.active_hours}h / {w.shift_hours}h
            </span>
          </div>
          <div className="w-16 text-right flex-shrink-0">
            <p className="text-sm font-bold tabular-nums" style={{ color: w.capacity_pct > 100 ? '#DC2626' : w.capacity_pct > 80 ? '#D97706' : '#059669' }}>{w.capacity_pct}%</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ========== Pipeline View ========== */
function PipelineView({ orders }) {
  const stages = ['confirmed', 'fabric_ordered', 'fabric_received', 'in_production', 'ready_for_trial', 'alterations_in_progress'];
  const grouped = {};
  stages.forEach(s => grouped[s] = []);
  orders.forEach(o => { if (grouped[o.status]) grouped[o.status].push(o); });
  return (
    <div className="flex gap-3 overflow-x-auto pb-4" data-testid="pipeline-view">
      {stages.map(stage => {
        const items = grouped[stage] || [];
        return (
          <div key={stage} className="min-w-[210px] max-w-[230px] flex-shrink-0">
            <div className="flex items-center gap-2 mb-2 px-1">
              <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>{ORDER_STATUS_LABELS[stage] || stage}</span>
              <Badge variant="outline" className="text-[9px] px-1 py-0">{items.length}</Badge>
            </div>
            <div className="space-y-2">
              {items.map(o => (
                <Link key={o.id} to={`/app/orders/${o.id}`} className="block p-2.5 rounded-lg border bg-white hover:shadow-md"
                  style={{ borderColor: o.is_express ? 'rgba(217,119,6,0.4)' : 'rgba(27,42,74,0.08)', transition: 'box-shadow 200ms', ...(o.is_express ? { borderWidth: '2px' } : {}) }} data-testid={`pipeline-order-${o.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1">
                      <span className="text-[11px] font-bold" style={{ color: 'var(--brand-navy)' }}>{o.order_number}</span>
                      {o.is_express && <span className="text-[7px] font-bold px-1 rounded" style={{ backgroundColor: '#D97706', color: '#fff' }}>EXPRESS</span>}
                    </div>
                    {o.required_by_date && <span className="text-[9px]" style={{ color: 'var(--ink-muted)' }}>{o.required_by_date}</span>}
                  </div>
                  <p className="text-[10px] font-medium" style={{ color: 'var(--navy-ink)' }}>{o.client_name}</p>
                  <p className="text-[9px]" style={{ color: 'var(--ink-muted)' }}>{o.garment_summary} ({o.item_count})</p>
                  {o.total_tasks > 0 && (
                    <div className="mt-1.5 flex items-center gap-1.5">
                      <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(27,42,74,0.06)' }}>
                        <div className="h-full rounded-full" style={{ width: `${(o.completed_tasks / o.total_tasks) * 100}%`, backgroundColor: '#059669', transition: 'width 300ms' }} />
                      </div>
                      <span className="text-[8px] font-semibold tabular-nums" style={{ color: 'var(--ink-muted)' }}>{o.completed_tasks}/{o.total_tasks}</span>
                    </div>
                  )}
                  {o.blocked_tasks > 0 && <Badge className="text-[8px] px-1 py-0 mt-1" style={{ backgroundColor: 'rgba(239,68,68,0.1)', color: '#DC2626' }}>{o.blocked_tasks} blocked</Badge>}
                </Link>
              ))}
              {items.length === 0 && (
                <div className="py-6 text-center border border-dashed rounded-lg" style={{ borderColor: 'rgba(27,42,74,0.1)' }}>
                  <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>No orders</p>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ========== QC Analytics View ========== */
const QC_TYPE_LABELS = { fabric_receipt: 'Fabric Receipt', pre_trial: 'Pre-Trial', post_alteration: 'Post-Alteration' };
const RESULT_COLORS = { pass: '#059669', flag: '#D97706', fail: '#DC2626' };

function QCAnalyticsView({ data }) {
  if (!data) return <p className="text-sm py-8 text-center" style={{ color: 'var(--ink-muted)' }}>Loading analytics...</p>;

  const { first_pass_yield, total_inspections, results_by_type, karigar_corrections, top_failure_points, monthly_trend } = data;

  // Build chart data for results by type
  const typeChartData = Object.entries(results_by_type || {}).map(([type, counts]) => ({
    name: QC_TYPE_LABELS[type] || type,
    Pass: counts.pass || 0,
    Flag: counts.flag || 0,
    Fail: counts.fail || 0,
  }));

  return (
    <div className="space-y-6" data-testid="qc-analytics-view">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="rounded-xl border border-border/80 bg-white" data-testid="kpi-first-pass-yield">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold tabular-nums" style={{ color: first_pass_yield >= 80 ? '#059669' : first_pass_yield >= 60 ? '#D97706' : '#DC2626' }}>
              {first_pass_yield}%
            </p>
            <p className="text-[10px] font-semibold mt-1 uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>First-Pass Yield</p>
          </CardContent>
        </Card>
        <Card className="rounded-xl border border-border/80 bg-white" data-testid="kpi-total-inspections">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold tabular-nums" style={{ color: 'var(--brand-navy)' }}>{total_inspections}</p>
            <p className="text-[10px] font-semibold mt-1 uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>Total Inspections</p>
          </CardContent>
        </Card>
        <Card className="rounded-xl border border-border/80 bg-white" data-testid="kpi-pass-rate">
          <CardContent className="p-4 text-center">
            {(() => {
              const allPass = Object.values(results_by_type || {}).reduce((s, c) => s + (c.pass || 0), 0);
              const allTotal = Object.values(results_by_type || {}).reduce((s, c) => s + (c.total || 0), 0);
              const rate = allTotal > 0 ? Math.round((allPass / allTotal) * 100) : 0;
              return <p className="text-3xl font-bold tabular-nums" style={{ color: '#059669' }}>{rate}%</p>;
            })()}
            <p className="text-[10px] font-semibold mt-1 uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>Overall Pass Rate</p>
          </CardContent>
        </Card>
        <Card className="rounded-xl border border-border/80 bg-white" data-testid="kpi-corrections">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold tabular-nums" style={{ color: '#DC2626' }}>
              {(karigar_corrections || []).reduce((s, k) => s + k.corrections, 0)}
            </p>
            <p className="text-[10px] font-semibold mt-1 uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>Total Corrections</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Monthly Trend Chart */}
        <Card className="rounded-xl border border-border/80 bg-white" data-testid="monthly-trend-chart">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>Monthly QC Trend</CardTitle>
          </CardHeader>
          <CardContent className="px-2 pb-4">
            {(monthly_trend || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={monthly_trend} barGap={2}>
                  <XAxis dataKey="month_short" tick={{ fontSize: 10, fill: '#6B7280' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#6B7280' }} axisLine={false} tickLine={false} allowDecimals={false} width={30} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e5e7eb' }}
                    formatter={(value, name) => [value, name]} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Bar dataKey="pass" name="Pass" stackId="a" fill="#059669" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="flag" name="Flag" stackId="a" fill="#D97706" />
                  <Bar dataKey="fail" name="Fail" stackId="a" fill="#DC2626" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-xs text-center py-8" style={{ color: 'var(--ink-muted)' }}>No inspection data yet</p>
            )}
          </CardContent>
        </Card>

        {/* Results by QC Type Chart */}
        <Card className="rounded-xl border border-border/80 bg-white" data-testid="results-by-type-chart">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>Results by QC Type</CardTitle>
          </CardHeader>
          <CardContent className="px-2 pb-4">
            {typeChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={typeChartData} barGap={2} layout="vertical">
                  <XAxis type="number" tick={{ fontSize: 10, fill: '#6B7280' }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: '#6B7280' }} axisLine={false} tickLine={false} width={100} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e5e7eb' }} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Bar dataKey="Pass" stackId="a" fill="#059669" />
                  <Bar dataKey="Flag" stackId="a" fill="#D97706" />
                  <Bar dataKey="Fail" stackId="a" fill="#DC2626" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-xs text-center py-8" style={{ color: 'var(--ink-muted)' }}>No inspection data yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Karigar Correction Rates Table */}
      <Card className="rounded-xl border border-border/80 bg-white" data-testid="karigar-corrections-table">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>Karigar Correction Rates</CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          {(karigar_corrections || []).length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b" style={{ borderColor: 'rgba(27,42,74,0.08)' }}>
                    <th className="text-left py-2 font-semibold" style={{ color: 'var(--ink-muted)' }}>Karigar</th>
                    <th className="text-center py-2 font-semibold" style={{ color: 'var(--ink-muted)' }}>Stitched</th>
                    <th className="text-center py-2 font-semibold" style={{ color: 'var(--ink-muted)' }}>Corrections</th>
                    <th className="text-center py-2 font-semibold" style={{ color: 'var(--ink-muted)' }}>Rate</th>
                    <th className="text-left py-2 font-semibold" style={{ color: 'var(--ink-muted)' }}>Garment Breakdown</th>
                  </tr>
                </thead>
                <tbody>
                  {karigar_corrections.map((k) => (
                    <tr key={k.worker_id} className="border-b last:border-0" style={{ borderColor: 'rgba(27,42,74,0.04)' }}>
                      <td className="py-2 font-medium" style={{ color: 'var(--navy-ink)' }}>{k.worker_name}</td>
                      <td className="text-center py-2 tabular-nums" style={{ color: 'var(--ink-muted)' }}>{k.total_stitched}</td>
                      <td className="text-center py-2 tabular-nums font-semibold" style={{ color: '#DC2626' }}>{k.corrections}</td>
                      <td className="text-center py-2">
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-bold"
                          style={{
                            backgroundColor: k.correction_rate > 20 ? 'rgba(239,68,68,0.08)' : k.correction_rate > 10 ? 'rgba(245,158,11,0.08)' : 'rgba(16,185,129,0.08)',
                            color: k.correction_rate > 20 ? '#DC2626' : k.correction_rate > 10 ? '#D97706' : '#059669',
                            borderColor: 'transparent',
                          }}>
                          {k.correction_rate}%
                        </Badge>
                      </td>
                      <td className="py-2">
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(k.garment_breakdown || {}).map(([g, count]) => (
                            <Badge key={g} variant="outline" className="text-[9px] px-1 py-0"
                              style={{ borderColor: (GARMENT_COLORS[g] || '#6B7280') + '40', color: GARMENT_COLORS[g] || '#6B7280' }}>
                              {GARMENT_LABELS[g] || g}: {count}
                            </Badge>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-xs text-center py-6" style={{ color: 'var(--ink-muted)' }}>No correction data available</p>
          )}
        </CardContent>
      </Card>

      {/* Top Failure Points Table */}
      <Card className="rounded-xl border border-border/80 bg-white" data-testid="top-failures-table">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>Top Failure Points</CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          {(top_failure_points || []).length > 0 ? (
            <div className="space-y-2">
              {top_failure_points.map((fp, idx) => {
                const maxTotal = top_failure_points[0]?.total || 1;
                return (
                  <div key={fp.check_name} className="flex items-center gap-3" data-testid={`failure-point-${idx}`}>
                    <span className="text-[10px] font-bold w-5 text-right tabular-nums" style={{ color: 'var(--ink-muted)' }}>#{idx + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-xs font-medium truncate" style={{ color: 'var(--navy-ink)' }}>{fp.check_name}</span>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                          {fp.fail > 0 && <span className="text-[10px] font-semibold" style={{ color: '#DC2626' }}>{fp.fail} fail</span>}
                          {fp.flag > 0 && <span className="text-[10px] font-semibold" style={{ color: '#D97706' }}>{fp.flag} flag</span>}
                        </div>
                      </div>
                      <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(27,42,74,0.04)' }}>
                        <div className="h-full rounded-full flex">
                          {fp.fail > 0 && (
                            <div style={{ width: `${(fp.fail / maxTotal) * 100}%`, backgroundColor: '#DC2626' }} />
                          )}
                          {fp.flag > 0 && (
                            <div style={{ width: `${(fp.flag / maxTotal) * 100}%`, backgroundColor: '#D97706' }} />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-center py-6" style={{ color: 'var(--ink-muted)' }}>No failure data yet - all checks passing!</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ========== Main Production Page ========== */
export default function ProductionPage() {
  const { user } = useAuth();
  const canEdit = user?.role === 'Admin' || user?.role === 'Production';
  const [view, setView] = useState('daily');
  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pipeline, setPipeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionModal, setActionModal] = useState(null);
  const [actionNotes, setActionNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [qcModal, setQcModal] = useState(null); // { task, qcType }
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [tasksRes, summaryRes, pipelineRes, altTasksRes] = await Promise.all([
        productionAPI.tasks({}),
        productionAPI.summary(),
        productionAPI.pipeline(),
        standaloneAlterationsAPI.productionTasks().catch(() => ({ data: [] })),
      ]);
      // Merge standalone alteration tasks into the main task list
      const orderTasks = tasksRes.data || [];
      const altTasks = altTasksRes.data || [];
      setTasks([...orderTasks, ...altTasks]);
      setSummary(summaryRes.data || {});
      setPipeline(pipelineRes.data || []);
    } catch (err) {
      console.error('Failed to fetch production data', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const res = await qcAPI.analytics();
      setAnalytics(res.data || null);
    } catch (err) {
      console.error('Failed to fetch QC analytics', err);
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fetch analytics data when analytics tab is selected
  useEffect(() => {
    if (view === 'analytics' && !analytics && !analyticsLoading) {
      fetchAnalytics();
    }
  }, [view, analytics, analyticsLoading, fetchAnalytics]);

  const workerList = useMemo(() => {
    return (summary?.worker_loads || [])
      .map(w => ({ id: w.id, name: w.name, role: w.role || 'karigar' }));
  }, [summary]);

  const handleAction = (task, action) => {
    if (action === 'start') {
      productionAPI.updateStatus(task.id, { status: 'in_progress' }).then(() => {
        toast.success(`${TASK_TYPE_LABELS[task.task_type]} started`);
        fetchData();
      }).catch(() => toast.error('Failed to start task'));
    } else if (action === 'complete') {
      productionAPI.updateStatus(task.id, { status: 'completed' }).then(() => {
        toast.success(`${TASK_TYPE_LABELS[task.task_type]} completed`);
        fetchData();
      }).catch(() => toast.error('Failed to complete task'));
    } else if (action === 'block') {
      setActionModal({ task, action: 'block' });
      setActionNotes('');
    } else if (action === 'unblock') {
      productionAPI.updateStatus(task.id, { status: 'queued' }).then(() => {
        toast.success('Task unblocked');
        fetchData();
      }).catch(() => toast.error('Failed to unblock'));
    } else if (action === 'qc_pre_trial') {
      setQcModal({ task, qcType: 'pre_trial' });
    } else if (action === 'outsource_send') {
      const today = new Date().toISOString().split('T')[0];
      productionAPI.outsourceSend(task.id, { outsource_sent_date: today }).then(() => {
        toast.success('Marked sent to vendor');
        fetchData();
      }).catch(() => toast.error('Failed'));
    } else if (action === 'outsource_receive') {
      setActionModal({ task, action: 'outsource_receive' });
      setActionNotes('');
    }
  };

  const handleReassign = async (taskId, newWorkerId) => {
    // Optimistic update
    setTasks(prev => prev.map(t => {
      if (t.id !== taskId) return t;
      const wk = workerList.find(w => w.id === newWorkerId);
      const isOutsource = wk?.role === 'outsourced_vendor';
      return {
        ...t,
        assigned_worker_id: newWorkerId,
        assigned_worker_name: wk?.name || '',
        is_outsourced: isOutsource,
        ...(isOutsource ? {} : { outsource_sent_date: null, outsource_expected_return: null, outsource_actual_return: null, outsource_cost: null }),
      };
    }));
    try {
      await productionAPI.reassign(taskId, { assigned_worker_id: newWorkerId });
      toast.success('Task reassigned');
      fetchData(); // refresh to get server state
    } catch {
      toast.error('Reassign failed');
      fetchData(); // revert
    }
  };

  const handleReorder = async (orderedTaskIds) => {
    // Optimistic: update priority_order in local state
    setTasks(prev => {
      const updated = [...prev];
      orderedTaskIds.forEach((id, idx) => {
        const task = updated.find(t => t.id === id);
        if (task) task.priority_order = idx + 1;
      });
      // Re-sort so UI reflects immediately
      updated.sort((a, b) => (a.priority_order || 9999) - (b.priority_order || 9999));
      return updated;
    });
    try {
      await productionAPI.reorder(orderedTaskIds);
      toast.success('Priority updated');
    } catch {
      toast.error('Reorder failed');
      fetchData(); // revert
    }
  };

  const submitAction = async () => {
    if (!actionModal) return;
    setSubmitting(true);
    try {
      if (actionModal.action === 'block') {
        await productionAPI.updateStatus(actionModal.task.id, { status: 'blocked', block_reason: actionNotes, notes: actionNotes });
        toast.success('Task blocked');
      } else if (actionModal.action === 'outsource_receive') {
        await productionAPI.outsourceReceive(actionModal.task.id, {
          outsource_actual_return: new Date().toISOString().split('T')[0],
          outsource_cost: parseFloat(actionNotes) || 0,
        });
        toast.success('Marked received from vendor');
      }
      setActionModal(null);
      fetchData();
    } catch {
      toast.error('Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  const activeTasks = useMemo(() => tasks.filter(t => {
    if (t.status === 'cancelled') return false;
    // Keep completed stitching/correction visible until QC is done (they show "Run QC" button)
    if (t.status === 'completed' && ['stitching', 'correction'].includes(t.task_type)) return true;
    if (t.status === 'completed') return false;
    return true;
  }), [tasks]);
  const s = summary || {};

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-5 gap-3">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-20" />)}</div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="production-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 no-print">
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold" style={{ color: 'var(--brand-navy)' }}>Production</h1>
          <HelpTooltip text="Today's workshop view. Drag cards between workers to reassign. Drag up/down to prioritise." testId="help-production" />
          <p className="text-sm mt-1" style={{ color: 'var(--ink-muted)' }}>Daily scheduler &middot; {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => {
            const doc = generateProductionSheetPDF(tasks, new Date());
            downloadPDF(doc, `Production-Sheet-${new Date().toISOString().slice(0,10)}.pdf`);
            toast.success('Production Sheet PDF downloaded');
          }} className="gap-1" data-testid="print-production-sheet">
            <Printer className="w-3.5 h-3.5" /> Download Sheet
          </Button>
          <Button variant="ghost" size="sm" onClick={fetchData} className="gap-1" data-testid="production-refresh"><RefreshCw className="w-3.5 h-3.5" /> Refresh</Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: 'Queued', value: s.queued ?? 0, color: '#6B7280', icon: Clock },
          { label: 'In Progress', value: s.in_progress ?? 0, color: '#2563EB', icon: Play },
          { label: 'Waiting Fabric', value: s.waiting_fabric ?? 0, color: '#9333EA', icon: Package },
          { label: 'Blocked', value: s.blocked ?? 0, color: '#DC2626', icon: XCircle },
          { label: 'Done Today', value: s.completed_today ?? 0, color: '#059669', icon: CheckCircle2 },
          { label: 'Outsourced', value: s.outsourced ?? 0, color: '#6B7280', icon: Building2, badge: (s.outsourced_overdue || 0) > 0 ? `${s.outsourced_overdue} overdue` : null, badgeColor: '#DC2626' },
        ].map(c => (
          <Card key={c.label} className="rounded-xl border border-border/80 bg-white" data-testid={`summary-${c.label.toLowerCase().replace(/\s/g, '-')}`}>
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: c.color + '12' }}>
                <c.icon className="w-4 h-4" style={{ color: c.color }} />
              </div>
              <div>
                <p className="text-xl font-bold tabular-nums" style={{ color: c.color }}>{c.value}</p>
                <p className="text-[10px] font-medium" style={{ color: 'var(--ink-muted)' }}>{c.label}</p>
                {c.badge && <p className="text-[9px] font-bold" style={{ color: c.badgeColor }}>{c.badge}</p>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Deadline Warnings */}
      {(s.deadline_warnings || []).length > 0 && (
        <Card className="rounded-xl border bg-white" style={{ borderColor: 'rgba(239,68,68,0.3)' }} data-testid="deadline-warnings">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4" style={{ color: '#DC2626' }} />
              <span className="text-xs font-bold" style={{ color: '#DC2626' }}>Deadline Warnings</span>
            </div>
            <div className="space-y-1">
              {s.deadline_warnings.map(w => (
                <Link key={w.order_id} to={`/app/orders/${w.order_id}`}
                  className="flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-red-50" style={{ transition: 'background 120ms' }}>
                  <span className="text-[11px] font-semibold" style={{ color: '#DC2626' }}>{w.order_number} — {w.client_name}</span>
                  <span className="text-[10px]" style={{ color: '#DC2626' }}>{w.remaining_hours}h remaining, {w.days_left}d left (need {w.est_days_needed}d)</span>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* View Tabs */}
      <div className="flex items-center gap-1 p-1 rounded-lg w-fit" style={{ backgroundColor: 'rgba(27,42,74,0.04)' }}>
        {[
          { key: 'daily', label: 'Daily', icon: LayoutGrid },
          { key: 'weekly', label: 'Capacity', icon: CalendarDays },
          { key: 'pipeline', label: 'Pipeline', icon: GitBranch },
          { key: 'analytics', label: 'QC Analytics', icon: Activity },
        ].map(tab => (
          <button key={tab.key} onClick={() => setView(tab.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold ${view === tab.key ? 'bg-white shadow-sm' : ''}`}
            style={{ color: view === tab.key ? 'var(--brand-navy)' : 'var(--ink-muted)', transition: 'all 150ms' }}
            data-testid={`view-tab-${tab.key}`}>
            <tab.icon className="w-3.5 h-3.5" /> {tab.label}
          </button>
        ))}
      </div>

      {/* View Content */}
      <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
        <CardContent className="p-4">
          {view === 'daily' && <DailyView tasks={activeTasks} workerList={workerList} onAction={handleAction} canEdit={canEdit} onReassign={handleReassign} onReorder={handleReorder} />}
          {view === 'weekly' && <WeeklyView workerLoads={s.worker_loads || []} />}
          {view === 'pipeline' && <PipelineView orders={pipeline} />}
          {view === 'analytics' && (analyticsLoading ? (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-4 gap-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-20" />)}</div>
              <Skeleton className="h-56" />
            </div>
          ) : (
            <QCAnalyticsView data={analytics} />
          ))}
        </CardContent>
      </Card>

      {/* Action Modal (Block / Outsource Receive) */}
      <Dialog open={!!actionModal} onOpenChange={() => setActionModal(null)}>
        <DialogContent className="max-w-sm" data-testid="task-action-modal">
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--brand-navy)' }}>
              {actionModal?.action === 'outsource_receive' ? 'Mark Received from Vendor' : 'Block Task'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            {actionModal?.action === 'outsource_receive' ? (
              <>
                <Label className="text-xs">Outsource Cost (₹)</Label>
                <Input type="number" value={actionNotes} onChange={(e) => setActionNotes(e.target.value)}
                  placeholder="Enter vendor cost..." className="mt-1 h-9 text-sm" data-testid="outsource-cost-input" />
                <p className="text-[10px] mt-1" style={{ color: 'var(--ink-muted)' }}>
                  This will mark the item received and trigger QC.
                </p>
              </>
            ) : (
              <>
                <Label className="text-xs">Reason</Label>
                <Textarea value={actionNotes} onChange={(e) => setActionNotes(e.target.value)}
                  placeholder="Enter reason..." rows={3} className="mt-1" data-testid="action-notes-input" />
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionModal(null)}>Cancel</Button>
            <Button onClick={submitAction} disabled={submitting}
              style={{ backgroundColor: actionModal?.action === 'outsource_receive' ? '#059669' : '#DC2626' }}
              data-testid="action-submit-btn">
              {submitting ? 'Saving...' : actionModal?.action === 'outsource_receive' ? 'Confirm Received' : 'Block Task'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QC Inspection Form */}
      {qcModal && (
        <QCInspectionForm
          open={!!qcModal}
          onClose={() => setQcModal(null)}
          qcType={qcModal.qcType}
          garmentType={qcModal.task.garment_type}
          orderItemId={qcModal.task.order_item_id}
          onComplete={(result) => {
            fetchData();
          }}
        />
      )}

      {/* Print styles injected via style tag */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          [data-testid="production-page"] { font-size: 11px; }
          [data-testid="production-page"] .rounded-xl { border-radius: 4px !important; box-shadow: none !important; page-break-inside: avoid; }
          button, [data-testid*="drag-handle"], [data-testid*="-btn"] { display: none !important; }
          @page { margin: 0.5cm; size: A4 landscape; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}
